import { CardInterface } from '@app/definitions/interfaces';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import { MAIN_DOMAIN, EXERCISE_TYPES, KATEX_FORMAT } from '@app/app.constants';
import { TaskService } from '@app/core/services/task.service';
import { TeacherService } from '@app/teacher/services/teacher.service';
import { Box, Lesson } from '@df/interfaces';
import { separateBy } from '@app/_utils/helpers';
import { delay, map, mergeMap, tap } from 'rxjs/operators';
import { empty, Observable } from 'rxjs';
import { sortBy } from 'lodash';
import { IExercise } from '@df/interfaces/exercise';
import { NEW_LINE_KATEX } from '@pipes/exercise-format.pipe';

@Component({
    selector: 'app-print',
    templateUrl: './print.component.html',
    styleUrls: ['./print.component.scss', '../../../../_pipes/exercise-format.pipe.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrintComponent implements OnInit, OnDestroy {
    lessonId: number;
    boxId: number;
    lesson;
    boxes: any;
    box: Box;
    print;
    errorMessage: { message: string; boxID: number } = {
        message: null,
        boxID: null
    };
    waitMessage: string = null;
    main_domain = MAIN_DOMAIN;
    isLessonPrint = false;
    isBoxPrint = false;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    static calculateCardsRows(box): number {
        let card_rows = 2;
        if (box.cards_format === 'A5') {
            card_rows = 1;
        }
        return card_rows;
    }

    constructor(
        private taskService: TaskService,
        private route: ActivatedRoute,
        private teacherService: TeacherService,
        private cd: ChangeDetectorRef
    ) {
        window.scrollTo(0, 0);
    }

    ngOnInit(): void {
        this.init();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    separateBy(cards: Array<any>, num: number): Array<any> {
        // console.log(cards);
        return separateBy(cards, num);
    }

    isMagicBox(exercise): boolean {
        if (exercise) {
            return exercise.type === EXERCISE_TYPES.EXERCISE_MAGIC_BOX;
        }
        return false;
    }

    isTypeWithText(exercise): boolean {
        if (exercise) {
            return (
                exercise.type === EXERCISE_TYPES.EXERCISE_CALCULATION ||
                exercise.type === EXERCISE_TYPES.EXERCISE_SEQUENCE ||
                exercise.type === EXERCISE_TYPES.EXERCISE_INSERT
            );
        }
        return false;
    }

    isTypePuzzle(exercise): boolean {
        const puzzleGroup = [EXERCISE_TYPES.EXERCISE_PUZZLE_TASK, EXERCISE_TYPES.EXERCISE_PUZZLE_EXAMPLE];
        if (exercise) {
            return puzzleGroup.includes(exercise.type);
        }
        return false;
    }

    isTypeWithQuestion(exercise): boolean {
        if (exercise) {
            return (
                exercise.type !== EXERCISE_TYPES.EXERCISE_CALCULATION &&
                exercise.type !== EXERCISE_TYPES.EXERCISE_SEQUENCE &&
                exercise.type !== EXERCISE_TYPES.EXERCISE_MAGIC_BOX
            );
        }
        return false;
    }

    isLogicType(exercise): boolean {
        if (exercise) {
            return exercise.type === EXERCISE_TYPES.EXERCISE_LOGIC || exercise.type === EXERCISE_TYPES.EXERCISE_TASK;
        }
        return false;
    }

    isInsertType(exercise): boolean {
        if (exercise) {
            return exercise.type === EXERCISE_TYPES.EXERCISE_INSERT;
        }
        return false;
    }

    isInsertEng(): boolean {
        const instruction =
            this.box && this.box.files && this.box.files[0].instruction ? this.box.files[0].instruction : null;
        return instruction && instruction === 'Выбери верный перевод';
    }

    getLogicText(exercise: IExercise): string {
        return (
            exercise.text +
            NEW_LINE_KATEX +
            (exercise.questions || []).map(({ text }) => text || '').join(NEW_LINE_KATEX)
        );
    }

    getQuestionsText(exercise: IExercise): string {
        return (exercise.questions || []).map(({ text }) => text || '').join(NEW_LINE_KATEX);
    }

    logicIncludeCasesConstruction(exercise: IExercise): boolean {
        return exercise.text.includes('begin{cases}');
    }

    isBoxWithLongExercises(card): boolean {
        return (
            this.isLogicType(card.exercises[0]) &&
            card.exercises &&
            card.exercises.some(ex => {
                return ex.text.length > 25;
            })
        );
    }

    getCorrectClass(box) {
        let css_class: string;
        switch (box.cards_format) {
            case 'A6':
                css_class = 'print-a6';
                break;
            case 'A5':
                css_class = 'print-a5';
                break;
            case 'A3':
                css_class = 'print-a3';
                break;
            case 'A7':
                css_class = 'print-a7';
                break;
        }
        return css_class;
    }

    calculateCardColumns(box) {
        let card_columns: number = 2;
        switch (box.cards_format) {
            case 'A6':
                card_columns = 2;
                break;
            case 'A5':
                card_columns = 2;
                break;
            case 'A3':
                card_columns = 3;
                break;
            case 'A7':
                card_columns = 4;
                break;
        }
        return card_columns;
    }

    calculateCardsPerTable(box) {
        return this.calculateCardColumns(box) * PrintComponent.calculateCardsRows(box);
    }

    calcRecursively(num: number, cards_per_table: number): number {
        let res: number;
        if (num < cards_per_table / 2) {
            const n = num + 1;
            res = this.calcRecursively(n, cards_per_table);
        } else {
            res = num;
        }
        return res;
    }

    hasImage(box: Box): boolean {
        return !!box.sell_character_obj && !!box.sell_character_obj.black_image;
    }

    getImageHeight(box) {
        let height = '70%';
        switch (box.cards_format) {
            case 'A6':
                height = '65%';
                break;
            case 'A5':
                height = '50%';
                break;
            case 'A3':
                height = '50%';
                break;
            case 'A7':
                height = '50%';
                break;
        }
        return height;
    }

    callPrint() {
        if (this.boxes) {
            print();
        }
    }

    private init(): void {
        this.getCardsRequest()
            .pipe(
                takeUntil(this.destroy$),
                mergeMap(() => this.fetchCards())
            )
            .subscribe(() => {
                this.cd.detectChanges();
            });
    }

    private getCardsRequest(): Observable<any> {
        const params = this.route.snapshot.params;

        const lessonId = params['lesson_id'];
        const boxId = params['box_id'];

        if (!lessonId && !boxId) {
            return empty();
        }

        if (boxId) {
            this.isBoxPrint = true;
            this.boxId = boxId;
            return this.taskService.getTaskBoxById(boxId).pipe(
                tap((box: Box) => {
                    this.box = box;
                    this.lessonId = box.lesson;
                })
            );
        }
        if (lessonId) {
            this.isLessonPrint = true;
            this.lessonId = lessonId;
            return this.teacherService.getLesson(lessonId).pipe(
                tap((lesson: Lesson) => {
                    this.lesson = lesson;
                })
            );
        }
    }

    private fetchCards(): Observable<any> {
        const targetId = this.isLessonPrint ? this.lessonId : this.boxId;
        return this.taskService.getPrintTask(targetId, this.isLessonPrint).pipe(
            map((response: any) => this.handleFetch(response)),
            delay(1000),
            takeUntil(this.destroy$)
        );
    }

    private handleFetch(response: any): void {
        let boxes = response.results;
        if (boxes) {
            for (const box of boxes) {
                box.cards = sortBy(box.cards, 'card_number');
                let total_cards: number;
                const old_card_length = box.cards.length;
                const box_cards_per_table = this.calculateCardsPerTable(box);
                const remainder = Math.round(box.cards.length / box_cards_per_table);
                if (box_cards_per_table * remainder < box.cards.length) {
                    total_cards = box_cards_per_table * (remainder + 1);
                } else {
                    total_cards = box_cards_per_table * remainder;
                }
                const divis = total_cards - box.cards.length;
                if (total_cards !== 0) {
                    const div = this.calcRecursively(divis, box_cards_per_table);
                    for (let i = 0; i < div; i++) {
                        if (box.cards.length >= old_card_length + divis) {
                            break;
                        }
                        const card = { card_number: 9999999999 };
                        box.cards.push(card);
                    }
                }
            }
            boxes = sortBy(boxes, 'title');
        } else {
            this.errorMessage.message = 'Произошла ошибка при загрузке вариантов';
        }
        this.boxes = boxes;
    }

    public withoutDivedir(exercise: IExercise): boolean {
        // Не показывать строку для ответа в случаях когда:
        let without = true;

        // 1. При типе упражнения Вставка
        without = this.isInsertType(exercise);

        return without;
    }

    checkLit(cardInstruction: string) {
        const inDevider = ['Прочитай без запинки', 'Прочитай стихи без запинки'];
        return inDevider.includes(cardInstruction);
    }

    isKatexFormat(exercise: IExercise): boolean {
        return exercise.format_type === KATEX_FORMAT;
    }

    public showHint(card: CardInterface, exercise: any): boolean {
        // Проверяем совпадает ли инструкция карточки с подсказкой
        // - если нет, то нужно показывать подсказку
        let show = false;

        if (
            (card.card_text !== exercise.hint && exercise.type === EXERCISE_TYPES.EXERCISE_CALCULATION) ||
            (card.card_text !== exercise.hint && exercise.type === EXERCISE_TYPES.EXERCISE_LOGIC) ||
            (card.card_text !== exercise.hint && exercise.type === EXERCISE_TYPES.EXERCISE_INSERT)
        ) {
            show = true;
        }

        return show;
    }

    public getListStyle(card: CardInterface) {
        if (card.card_text && card.card_text.includes('Сколько точек')) {
            return { alignItems: 'center' };
        }
    }
}
